<script setup lang="ts">
import { useRouter } from "vue-router";
import { useMachine, useSelector } from "@xstate/vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  EditableArea,
  EditableCancelTrigger,
  EditableInput,
  EditablePreview,
  EditableRoot,
  EditableSubmitTrigger,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
  ToolbarButton,
  ToolbarRoot,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from "reka-ui";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import VoiceTranscriptUtterances from "@/components/CasePanels/RecordView/VoiceTranscript/VoiceTranscriptUtterances.vue";

import { useVoiceMachineStore } from "@/stores/voice-machine-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { SummaryNotesItemLogic } from "@/machines/summary-notes-item-machine";

import type { TranscriptAnnotation } from "@/models/Annotation";
import type { SummaryNote, SummaryNoteModified } from "@/models/SummaryNote";

withDefaults(defineProps<{
  summaryNote: SummaryNoteModified;
  category: string;
  isAdded: boolean;
  isFirst: boolean;
  isLast: boolean;
  isMoving: boolean;
  showCitations: boolean;
  isAnyMutating: boolean;
  isAnyMutatingInCategory: boolean;
  canManageSummaryNotes: boolean;
  speakerIdToName: Map<string, string>;
}>(), {  })
const emit = defineEmits<{
  (e: "moveUp"): void;
  (e: "moveDown"): void;
  (e: "delete"): void;
  (e: "refreshList", dp: SummaryNote): void;
}>();

const $router = useRouter();
const routeParams = useCaseRouteParams();

const editableRootRef = useTemplateRef<InstanceType<typeof EditableRoot>>("editable-root");
const editableTextareaRef = useTemplateRef<HTMLTextAreaElement>("editable-textarea");

const { actorRef, send } = useMachine(SummaryNotesItemLogic, {
  input: { params: __props.summaryNote, pollCitations: __props.showCitations },
});

actorRef.on("COMMIT_FAILED", () => editableRootRef.value?.edit());
actorRef.on("COMMIT_SUCCESS", ({ summaryNote }) => {
  emit("refreshList", summaryNote);
  showEvidence.value = true;
  editableRootKey.value = Math.random().toString(36);
});

const isSaving = useSelector(actorRef, (snapshot) => snapshot.matches("committing"));
const isEditing = useSelector(actorRef, (snapshot) => snapshot.matches("editing"));
const noteError = useSelector(actorRef, (snapshot) => snapshot.context.error);
const isLoadingCitations = useSelector(actorRef, (snapshot) => snapshot.matches("polling"));

const showEvidence = ref(__props.isAdded);
const editableRootKey = ref<string>();

const summaryText = computed(() => {
  if (__props.speakerIdToName.size < 1) return __props.summaryNote.summaryText;
  return __props.summaryNote.summaryText.replace(
    new RegExp(`\\b${[...__props.speakerIdToName.keys()].join("\\b|\\b")}\\b`, "g"),
    (speakerId) => __props.speakerIdToName.get(speakerId) ?? speakerId,
  );
});

function toggleShowEvidence(open = !showEvidence.value) {
  showEvidence.value = open;
}

function onSubmit(value: string | null | undefined) {
  const isNotValid = !(editableTextareaRef.value?.reportValidity() ?? true);
  value = value?.trim() ?? "";

  if (isNotValid || value.length < 1) editableRootRef.value?.edit();
  else send({ type: "COMMIT", value });
}

function handleStateChange(state: "cancel" | "submit" | "edit") {
  switch (state) {
    case "edit":
      send({ type: "START_EDITING" });
      break;
    case "cancel":
      editableRootKey.value = Math.random().toString(36);
      send({ type: "RESET", input: { params: __props.summaryNote, pollCitations: __props.showCitations } });
      break;
  }
}

function transcriptGoToCitation(citation: TranscriptAnnotation) {
  if (!("startTime" in citation)) return;

  const { brand, casename } = routeParams.value;

  const startTime = +citation.startTime.toFixed(2);
  const endTime = +citation.endTime.toFixed(2);

  $router.push({
    name: "casefiletimerange",
    params: { brand, casename, casefile: __props.summaryNote.fileReference, startTime, endTime },
  });

  useVoiceMachineStore().setTimeRange("summary-notes", __props.summaryNote.fileReference, startTime, endTime);
}

defineExpose({ category: __props.category, isEditing, reference: __props.summaryNote.reference });

watch([() => __props.summaryNote, () => __props.showCitations], ([newDataPoint, newShowCitations]) =>
  send({
    type: "RESET",
    input: { pollCitations: newShowCitations, params: newDataPoint },
  }),
);

watch(summaryText, () => {
  if (isEditing.value) return;
  editableRootKey.value = Math.random().toString(36);
});
</script>
<template>
  <TooltipProvider :disabled="!canManageSummaryNotes" :delay-duration="0">
    <TooltipRoot>
      <component
        :is="showCitations ? CollapsibleRoot : 'div'"
        :open="showCitations ? showEvidence : undefined"
        class="tw-group tw-relative tw-col-span-full tw-grid tw-grid-cols-subgrid *:tw-gap-1 *:tw-px-[--padding] *:tw-py-1 hover:*:tw-bg-gray-200"
        @update:open="toggleShowEvidence"
      >
        <TooltipTrigger as-child>
          <div
            class="tw-sticky -tw-top-px tw-z-[10000] tw-col-span-full tw-grid tw-grid-cols-subgrid tw-items-baseline tw-bg-white [&:not([data-state=closed])]:tw-bg-gray-200"
          >
            <div aria-hidden="true" class="tw-w-4">
              <i class="fas fa-circle fa-2xs" />
            </div>
            <EditableRoot
              ref="editable-root"
              :key="editableRootKey"
              select-on-focus
              submit-mode="enter"
              activation-mode="dblclick"
              class="tw-flex tw-grow tw-items-center tw-gap-2"
              :default-value="summaryText"
              :readonly="isSaving || isMoving || !canManageSummaryNotes"
              @interact-outside.prevent
              @submit="onSubmit"
              @update:state="handleStateChange"
            >
              <EditableArea
                id="editable-area"
                class="tw-flex tw-min-h-[--height] tw-flex-1 tw-flex-col tw-justify-center [--height:theme(spacing.8)]"
              >
                <EditablePreview />
                <EditableInput as-child>
                  <textarea
                    ref="editable-textarea"
                    required
                    :class="{ 'tw-border-red-400 tw-outline-red-400': noteError != null }"
                    class="tw-size-full tw-min-h-[--height] tw-rounded-md tw-border tw-border-gray-400 tw-px-2.5 tw-leading-[--height] [field-sizing:content]"
                  />
                </EditableInput>
                <div v-if="isSaving || isMoving" class="tw-flex tw-items-center tw-gap-1 tw-text-blue-500">
                  <i class="fas fa-sm fa-spin fa-circle-notch"></i>
                  {{ isMoving ? "Moving" : "Validating" }}
                </div>
                <div v-else-if="noteError != null" class="tw-col-span-full tw-text-red-400">
                  <i class="fas fa-triangle-exclamation" />
                  {{ noteError.content }}
                </div>
              </EditableArea>
              <div v-if="isEditing" class="tw-flex tw-items-center tw-gap-1">
                <EditableSubmitTrigger
                  class="tw-rounded-md tw-px-2 tw-py-1 tw-text-green-600 tw-underline tw-underline-offset-2 hover:tw-bg-green-800/75 hover:tw-text-white"
                >
                  Save
                </EditableSubmitTrigger>
                <EditableCancelTrigger
                  class="tw-rounded-md tw-px-2 tw-py-1 tw-text-red-600 tw-underline tw-underline-offset-2 hover:tw-bg-red-800/75 hover:tw-text-white"
                >
                  Cancel
                </EditableCancelTrigger>
              </div>
            </EditableRoot>
            <div v-if="showCitations" class="empty:tw-hidden">
              <span
                v-if="isLoadingCitations"
                class="tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-1 tw-text-gray-800"
              >
                <i aria-hidden="true" class="fas fa-xs fa-circle-notch fa-spin" /> Refreshing
              </span>
              <StringTooltip
                v-else-if="summaryNote?.awaitingCitations"
                align="end"
                item="Click to manually fetch Evidence updates"
              >
                <button
                  type="button"
                  class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-1 tw-text-gray-600 hover:tw-bg-gray-800/75 hover:tw-text-white disabled:tw-cursor-auto"
                  @click="send({ type: 'REFRESH_SUMMARY_NOTE' })"
                >
                  <i aria-hidden="true" class="fas fa-xs fa-rotate-right" />
                  Refresh
                </button>
              </StringTooltip>
            </div>
          </div>
        </TooltipTrigger>
        <CollapsibleContent
          v-if="showCitations"
          tabindex="0"
          aria-label="Citations"
          class="tw-col-span-full tw-grid-cols-subgrid tw-bg-gray-100/50 tw-pb-1 *:tw-col-start-2 empty:tw-hidden [&:not(:empty,[hidden])]:tw-grid"
        >
          <div v-for="[key, citations] in summaryNote?.citations" :key="key" class="tw-space-y-1">
            <p
              class="tw-font-medium"
              :class="{
                'tw-text-green-600': key === 'Supporting',
                'tw-text-red-600': key === 'Contrary',
                'tw-text-gray-600': key === 'Association',
              }"
            >
              {{ `${key} Evidence` }}
            </p>
            <div class="tw-ml-3 tw-space-y-1.5">
              <div
                v-for="(citation, citationIdx) in citations"
                :key="`${citation.supporting}-${citationIdx}`"
                class="tw-cursor-pointer tw-rounded-md tw-border tw-border-gray-400 tw-bg-white tw-py-1 hover:tw-brightness-95"
                @click="() => transcriptGoToCitation(citation.contentJson)"
              >
                <VoiceTranscriptUtterances
                  :file-reference="summaryNote.fileReference"
                  :list="citation.contentJson.utteranceList"
                />
              </div>
            </div>
          </div>
        </CollapsibleContent>
        <TooltipPortal>
          <TooltipContent as-child hide-when-detached avoid-collisions>
            <ToolbarRoot
              class="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-gray-600 tw-bg-white tw-p-2 tw-shadow-sm"
            >
              <StringTooltip
                v-slot="{ disabled }"
                item="Move Up"
                :root="{ disabled: isFirst || isEditing || isAnyMutatingInCategory }"
              >
                <ToolbarButton
                  class="tw-grid tw-aspect-square tw-h-5 tw-cursor-pointer tw-place-items-center tw-rounded-full hover:tw-bg-gray-200 disabled:tw-pointer-events-none disabled:tw-cursor-auto disabled:tw-bg-gray-200 disabled:tw-text-gray-700 disabled:tw-opacity-85"
                  :disabled="disabled"
                  @click="!disabled && $emit('moveUp')"
                >
                  <i aria-hidden class="fas fa-lg fa-caret-up" />
                </ToolbarButton>
              </StringTooltip>
              <StringTooltip
                v-slot="{ disabled }"
                item="Move Down"
                :root="{ disabled: isLast || isEditing || isAnyMutatingInCategory }"
              >
                <ToolbarButton
                  class="tw-grid tw-aspect-square tw-h-5 tw-cursor-pointer tw-place-items-center tw-rounded-full hover:tw-bg-gray-200 disabled:tw-pointer-events-none disabled:tw-cursor-auto disabled:tw-bg-gray-200 disabled:tw-text-gray-700 disabled:tw-opacity-85"
                  :disabled="disabled"
                  @click="!disabled && $emit('moveDown')"
                >
                  <i aria-hidden class="fas fa-lg fa-caret-down" />
                </ToolbarButton>
              </StringTooltip>
              <PopoverRoot>
                <PopoverAnchor>
                  <StringTooltip
                    v-slot="{ disabled }"
                    item="Delete"
                    :root="{ disabled: summaryNote?.awaitingCitations || isEditing || isAnyMutating }"
                  >
                    <PopoverTrigger as-child>
                      <ToolbarButton
                        class="tw-grid tw-aspect-square tw-h-5 tw-cursor-pointer tw-place-items-center tw-rounded-full tw-text-red-500 hover:tw-bg-gray-200 disabled:tw-cursor-auto disabled:tw-bg-gray-100 disabled:tw-text-gray-500 disabled:tw-opacity-85"
                        :disabled="disabled"
                        @click.stop
                      >
                        <i aria-hidden="true" class="fas fa-trash" />
                      </ToolbarButton>
                    </PopoverTrigger>
                  </StringTooltip>
                </PopoverAnchor>
                <PopoverContent
                  align="start"
                  avoid-collisions
                  hide-when-detached
                  class="tw-z-[1000] tw-flex tw-select-none tw-flex-col tw-items-end tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-bg-white tw-p-2 tw-text-xs tw-shadow"
                >
                  <p class="tw-max-w-xs">Are you sure you want to delete this summary note?</p>
                  <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <PopoverClose class="tw-btn-md-gray">Cancel</PopoverClose>
                    <button
                      type="button"
                      class="tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-red-500/40"
                      @click="$emit('delete')"
                    >
                      Delete
                    </button>
                  </div>
                </PopoverContent>
              </PopoverRoot>
              <template v-if="showCitations">
                <CollapsibleTrigger
                  v-if="Object.keys(summaryNote.citations ?? {}).length > 0"
                  class="tw-rounded-md tw-px-1 tw-py-0.5 tw-font-medium tw-underline hover:tw-underline-offset-2 disabled:tw-cursor-auto"
                >
                  {{ showEvidence ? "Hide" : "Show" }} Evidence
                </CollapsibleTrigger>
                <span v-else class="tw-cursor-pointer tw-select-none tw-text-gray-600">No Evidence found</span>
              </template>
            </ToolbarRoot>
          </TooltipContent>
        </TooltipPortal>
      </component>
    </TooltipRoot>
  </TooltipProvider>
</template>
